import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Header } from '../../../../components/Header/Header'
import { Title } from '../../../../components/Title/Title'
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Button, Collapse, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import AddIcon from '@mui/icons-material/AddRounded';
import RemoveIcon from '@mui/icons-material/DoNotDisturbOnRounded';
import axiosInstance from '../../../../configs/axiosInstance';
import { fetchData } from '../Cities/helpers/fetchData';
import { citiesRef } from '../../../../utils/collectionRefs';
import OrderGrid from '../../../../components/OrderGrid ';
import { kassaTransactionColumns } from '../KassIn/constants/getKassaTransactionColumns';
import dayjs from 'dayjs';

const OSV = ({ pageTitle }) => {
  const today = dayjs();
  const defaultDateStart = today.subtract(3, 'day');
  const defaultDateEnd = today.add(1, 'day');
  const isFirstRender = useRef(true)

  const { handleSubmit, control, reset, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      dateStart: defaultDateStart.toDate(),
      dateEnd: defaultDateEnd.toDate(),
      city_id: 0,
    },
  });

  const [categories, setCategories] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null); // ID категории, которую раскрыли
  const [subCategories, setSubCategories] = useState({});
  const [cities, setCities] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [loadingCities, setLoadingCities] = useState(true)

  const [expandedType, setExpandedType] = useState(null)

  const dateStart = useWatch({ control, name: 'dateStart' });
  const dateEnd = useWatch({ control, name: 'dateEnd' });
  const cityId = useWatch({ control, name: 'city_id' });

  const summaryData = [
    {
      type: 'income',
      label: 'Доход',
      total: categories
        .filter((cat) => cat.type === 'income')
        .reduce((sum, cat) => sum + cat.total, 0),
    },
    {
      type: 'expense',
      label: 'Расход',
      total: categories
        .filter((cat) => cat.type === 'expense')
        .reduce((sum, cat) => sum + cat.total, 0),
    },
  ];

  const handleReset = () => {
    reset({
      dateStart: defaultDateStart.toDate(),
      dateEnd: defaultDateEnd.toDate(),
      city_id: 0,
    })
  }

  const fetchCategories = async (data) => {
    if (!data.dateStart || !data.dateEnd || data.city_id === undefined) {
      console.error('Некорректные данные для запроса:', data);
      return;
    }
    try {
      const formattedDateStart = dayjs(data.dateStart).format('YYYY-MM-DD');
      const formattedDateEnd = dayjs(data.dateEnd).format('YYYY-MM-DD');
      const response = await axiosInstance.post('/core/get_osv_by_cat/', {
        dateStart: formattedDateStart,
        dateEnd: formattedDateEnd,
        city_id: data.city_id,
      });

      const incomes = response.data.incomes.map(item => ({
        type: 'income',
        id: item.category.id,
        name: item.category.name,
        total: item.total,
        bezNalTotal: item.bezNalTotal,
        nalichkaTotal: item.nalichkaTotal,
      }));

      const expenses = response.data.expenses.map(item => ({
        type: 'expense',
        id: item.category.id,
        name: item.category.name,
        total: item.total,
      }));
      setCategories([...incomes, ...expenses]);
    } catch (error) {
      console.error('Ошибка при загрузке категорий:', error);
    }
  };

  const handleLoadSubCategories = async (categoryId) => {
    try {
      const { dateStart, dateEnd, city_id } = watch();

      if (subCategories[categoryId]) {
        setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
        return;
      }
      const formattedDateStart = dayjs(dateStart).format('YYYY-MM-DD');
      const formattedDateEnd = dayjs(dateEnd).format('YYYY-MM-DD');
      const response = await axiosInstance.post('/core/get_osv_by_sub_cat/', {
        category_id: categoryId,
        dateStart: formattedDateStart,
        dateEnd: formattedDateEnd,
        city_id,
      });

      const incomes = response.data.incomes.map(item => ({
        type: 'income',
        id: item.type.id,
        name: item.type.name,
        total: item.total,
      }));

      const expenses = response.data.expenses.map(item => ({
        type: 'expense',
        id: item?.type?.id,
        name: item?.type?.name,
        total: item.total,
      }));

      setSubCategories(prev => ({
        ...prev,
        [categoryId]: [...incomes, ...expenses],
      }));

      setExpandedCategory(categoryId);
    } catch (error) {
      console.error('Ошибка при загрузке подкатегорий:', error);
    }
  };
  const fetchTransactions = async (typeId, page = 1, pageSize) => {
    try {
      const response = await axiosInstance.get('/core/kassaTransaction/', {
        params: {
          type: typeId,
          page: page,
          page_size: pageSize,
          start_date: dayjs(dateStart).format('YYYY-MM-DD'),
          end_date: dayjs(dateEnd).format('YYYY-MM-DD'),
        }
      });
      return response.data;
    } catch (error) {
      console.error('Ошибка при загрузке транзакций:', error);
      throw error;
    }
  };

  const loadTransactions = async (typeId, page = 1, pageSize) => {
    try {
      const data = await fetchTransactions(typeId, page, pageSize);
      setTransactions(data.results);
      setTotalRowCount(data.count);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    fetchCategories({
      dateStart,
      dateEnd,
      city_id: cityId,
    });
  }, [dateStart, dateEnd, cityId]);

  useEffect(() => {
    setSubCategories({});
    setExpandedCategory(null);
  }, [dateStart, dateEnd, cityId]);

  useEffect(() => {
    if (expandedCategory) {
      handleLoadSubCategories(expandedCategory);
    }
  }, [dateStart, dateEnd, cityId]);

  useEffect(() => {
    if (selectedTypeId) {
      loadTransactions(selectedTypeId, currentPage, pageSize);
    }
  }, [dateStart, dateEnd, cityId, selectedTypeId, currentPage, pageSize]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoadingCities(true);
        const citiesData = await fetchData(citiesRef, 'name', 'asc');
        setCities(citiesData);
        setLoadingCities(false);
      } catch (error) {
        console.error('Ошибка при загрузке городов:', error);
        setLoadingCities(false);
      }
    };

    fetchCities();
  }, []);

  const totalIncome = categories
    .filter((cat) => cat?.type === 'income')
    .reduce((sum, cat) => sum + cat.total, 0);

  const totalExpense = categories
    .filter((cat) => cat?.type === 'expense')
    .reduce((sum, cat) => sum + cat.total, 0);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };
  const columns = kassaTransactionColumns()

  return (
    <div className='containerr'>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Header previous='' initial='ОСВ' />
      <Title
        title='ОСВ'
        icon={<SummarizeOutlinedIcon fontSize='meduim' />}
      />
      <div className="container-inner">
        <Stack direction='row' gap={1}>
          <form className="search-form" onSubmit={handleSubmit(fetchCategories)}>
            <div className="search-input-block">

              <Stack direction='row' gap={2}>
                <Controller
                  name='dateStart'
                  control={control}
                  defaultValue={null}
                  rules={{
                    required: "Обязательное поле",
                    validate: (value) => value !== null || "Дата начала должна быть выбрана",
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      label="Начало"
                      value={dayjs(field.value)}
                      onChange={(date) => field.onChange(date?.toDate())}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  name='dateEnd'
                  control={control}
                  defaultValue={null}
                  rules={{
                    required: "Обязательное поле",
                    validate: (value) => value !== null || "Дата конца должна быть выбрана",
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      label="Конец"
                      value={dayjs(field.value)} // Преобразуем значение в dayjs
                      onChange={(date) => field.onChange(date?.toDate())}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  )}
                />
              </Stack>
              <Stack direction='row' gap={2}>
                <Controller
                  name='city_id'
                  control={control}
                  defaultValue=''
                  rules={{ required: "Обязательное поле" }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Город'
                      size='small'
                      error={!!error}
                      helperText={error ? error.message : ""}
                      select
                      fullWidth
                      disabled={loadingCities}
                    >
                      <MenuItem value={0}>Все</MenuItem>
                      {cities?.map((option) => (
                        <MenuItem key={option.name} value={parseInt(option.id)}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Stack>
              <Stack direction='row' gap={1}>
                <Button
                  variant='contained'
                  onClick={handleReset}
                  color='inherit'
                >
                  Сбросить фильтры
                </Button>
              </Stack>
            </div>
          </form>
        </Stack>
        <div className="orders-card-wrapper">
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Тип</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Сумма</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Действия</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryData.map((item) => (
                  <React.Fragment key={item.type}>
                    <TableRow style={{ backgroundColor: item.type === 'income' ? '#70bb69' : '#d02626bf' }}>
                      <TableCell>
                        <strong>{item.label}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{item.total.toLocaleString()} сом</strong>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color={expandedType === item.type ? 'error' : 'primary'}
                          onClick={() =>
                            setExpandedType((prev) => (prev === item.type ? null : item.type))
                          }
                        >
                          {expandedType === item.type ? <RemoveIcon /> : <AddIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {expandedType === item.type && (
                      <TableRow>
                        <TableCell colSpan={3} style={{ padding: 0 }}>
                          <Collapse in={expandedType === item.type} timeout="auto" unmountOnExit>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Категория</TableCell>
                                  <TableCell>Сумма</TableCell>
                                  <TableCell>Действие</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {categories
                                  ?.filter((cat) => cat.type === item.type)
                                  .map((cat, index) => (
                                    <React.Fragment key={index}>
                                      {/* Основная строка категории */}
                                      <TableRow style={{ backgroundColor: cat.type === 'income' ? '#e8ffe6' : '#ffe2e2' }}>
                                        <TableCell>{cat?.name} {cat.type === 'income' ? 'в т.ч.:' : ''}</TableCell>
                                        <TableCell>
                                          <div>
                                            <strong>{cat.total.toLocaleString()} сом</strong>
                                          </div>
                                          {cat.type === 'income' && (
                                            <div style={{ fontSize: '0.9em', color: '#555' }}>
                                              <div>Безнал: {cat.bezNalTotal?.toLocaleString() || 0} сом</div>
                                              <div>Наличка: {cat.nalichkaTotal?.toLocaleString() || 0} сом</div>
                                            </div>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <IconButton
                                            color={expandedCategory === cat.id ? "error" : "primary"}
                                            onClick={() => handleLoadSubCategories(cat.id)}
                                            disabled={!cat.id}
                                          >
                                            {expandedCategory === cat.id ? <RemoveIcon /> : <AddIcon />}
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>

                                      {/* Подкатегории */}
                                      <TableRow>
                                        <TableCell colSpan={4} style={{ padding: 0 }}>
                                          <Collapse in={expandedCategory === cat.id} timeout="auto" unmountOnExit>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow style={{ backgroundColor: '#ececec' }}>
                                                  <TableCell>Подкатегории</TableCell>
                                                  <TableCell>Сумма</TableCell>
                                                  <TableCell>Действия</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {subCategories[cat.id]?.map((sub) => (
                                                  <React.Fragment key={sub.id}>
                                                    <TableRow>
                                                      <TableCell>{sub.name}</TableCell>
                                                      <TableCell>{sub.total.toLocaleString()} сом</TableCell>
                                                      <TableCell>
                                                        <IconButton
                                                          color={selectedTypeId === sub.id ? "error" : "primary"}
                                                          onClick={() => setSelectedTypeId((prev) => (prev === sub.id ? null : sub.id))}
                                                          disabled={!sub.id}
                                                        >
                                                          {selectedTypeId === sub.id ? <RemoveIcon /> : <AddIcon />}
                                                        </IconButton>
                                                      </TableCell>
                                                    </TableRow>
                                                    {selectedTypeId === sub.id && (
                                                      <TableRow>
                                                        <TableCell colSpan={3} style={{ padding: 0 }}>
                                                          <OrderGrid
                                                            orders={transactions || []}
                                                            columns={columns}
                                                            size={pageSize}
                                                            page={currentPage - 1}
                                                            totalRowCount={totalRowCount}
                                                            loading={false}
                                                            usePagination={true}
                                                            onPageChange={handlePageChange}
                                                            onPageSizeChange={handlePageSizeChange}
                                                          />
                                                        </TableCell>
                                                      </TableRow>
                                                    )}
                                                  </React.Fragment>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                    </React.Fragment>
                                  ))}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  )
}

export default OSV